import { getSupabase } from "./supabase";

// ─── Configuration ─────────────────────────────────────────────────────────

let r2PublicUrl = "";

/**
 * Configure the R2 storage adapter. Call once at app startup after initSupabase().
 *
 * @example
 * // Web:
 * configureR2Storage({ publicUrl: R2_PUBLIC_URL });
 * // Mobile:
 * configureR2Storage({ publicUrl: R2_PUBLIC_URL });
 */
export function configureR2Storage(config: { publicUrl: string }): void {
  r2PublicUrl = config.publicUrl.replace(/\/+$/, ""); // strip trailing slashes
}

// ─── Errors ────────────────────────────────────────────────────────────────

export class R2StorageError extends Error {
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, code: string, details?: unknown) {
    super(message);
    this.name = "R2StorageError";
    this.code = code;
    this.details = details;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function requireSupabase() {
  const supabase = getSupabase();
  if (!supabase) {
    throw new R2StorageError(
      "Supabase client not initialized",
      "CLIENT_NOT_INITIALIZED",
    );
  }
  return supabase;
}

/**
 * Call the storage-presign edge function via the Supabase client.
 * Uses supabase.functions.invoke() which automatically handles
 * Authorization (JWT) and apikey (anon key) headers.
 *
 * Error mapping:
 * - HTTP 403 with body.error === "Storage limit reached" →
 *   R2StorageError(code: "STORAGE_LIMIT_REACHED")
 * - Any other failure → R2StorageError(code: "PRESIGN_ERROR")
 */
async function invokePresign(body: Record<string, unknown>): Promise<unknown> {
  const supabase = requireSupabase();
  const { data, error } = await supabase.functions.invoke("storage-presign", {
    body,
  });

  if (error) {
    // FunctionsHttpError exposes the original Response on `error.context`.
    // Try to read the JSON body so we can surface a typed quota error.
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const parsed = (await ctx.clone().json()) as {
          error?: string;
          current_bytes?: number;
          limit_bytes?: number;
        };
        if (ctx.status === 403 && parsed?.error === "Storage limit reached") {
          throw new R2StorageError(
            parsed.error,
            "STORAGE_LIMIT_REACHED",
            { currentBytes: parsed.current_bytes, limitBytes: parsed.limit_bytes },
          );
        }
      } catch (parseErr) {
        if (parseErr instanceof R2StorageError) throw parseErr;
        // fall through to generic error below
      }
    }

    throw new R2StorageError(
      `Presign call failed: ${error.message}`,
      "PRESIGN_ERROR",
      error,
    );
  }

  return data;
}

// ─── Public API ────────────────────────────────────────────────────────────

export interface PresignedUploadResult {
  uploadUrl: string;
  objectKey: string;
  publicUrl: string;
  fileSizeBytes: number;
}

/**
 * Request a presigned PUT URL from the storage-presign edge function.
 *
 * @param bucket - "photos" or "documents"
 * @param objectKey - Full storage path including tenant prefix
 * @param contentType - MIME type of the file
 * @param contentLength - File size in bytes
 */
export async function requestPresignedUpload(
  bucket: string,
  objectKey: string,
  contentType: string,
  contentLength: number,
): Promise<PresignedUploadResult> {
  const data = await invokePresign({
    action: "upload",
    bucket,
    objectKey,
    contentType,
    contentLength,
  });

  return data as PresignedUploadResult;
}

/**
 * Upload a file to R2 using a presigned PUT URL.
 *
 * @param uploadUrl - The presigned URL from requestPresignedUpload
 * @param body - File, Blob, or ArrayBuffer to upload
 * @param contentType - MIME type matching the presigned URL
 */
export async function uploadToR2(
  uploadUrl: string,
  body: File | Blob | ArrayBuffer,
  contentType: string,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new R2StorageError(
      `Upload to R2 failed: ${text}`,
      "UPLOAD_ERROR",
    );
  }
}

/**
 * Delete a file from R2 via the storage-presign edge function.
 *
 * @param bucket - "photos" or "documents"
 * @param objectKey - Full storage path including tenant prefix
 */
export async function requestDelete(
  bucket: string,
  objectKey: string,
): Promise<void> {
  await invokePresign({
    action: "delete",
    bucket,
    objectKey,
  });
}

/**
 * Get the public URL for a stored file.
 *
 * Handles dual-format paths:
 * - Legacy full Supabase URLs (starts with "http") → returned as-is
 * - R2 object keys (relative paths) → prefixed with R2_PUBLIC_URL
 *
 * @param path - Storage path or full URL
 */
export function getPublicUrl(path: string): string {
  if (!path) return "";
  // Legacy: full Supabase Storage URLs still in DB
  if (path.startsWith("http")) return path;
  // New: R2 object key → public URL
  return `${r2PublicUrl}/${path}`;
}

/**
 * Extract the R2 object key (storage path) from a public URL.
 *
 * Returns the path portion after the R2 public URL base, or undefined
 * if the URL doesn't belong to the configured R2 bucket.
 *
 * @example
 * // R2 URL → storage path
 * extractStoragePath("https://pub-xxx.r2.dev/tenant/documents/assets/id/file.pdf")
 * // → "tenant/documents/assets/id/file.pdf"
 */
export function extractStoragePath(url: string): string | undefined {
  if (!r2PublicUrl || !url) return undefined;
  const prefix = `${r2PublicUrl}/`;
  if (url.startsWith(prefix)) {
    return url.slice(prefix.length);
  }
  return undefined;
}
